export const PRESENTATION_DEFAULTS = Object.freeze({
  schema_version: 1,
  presenter_mode: "mascot",
  avatar_package_ref: null,
  avatar_package_version: null,
  theme_mode: "system",
  brand_reference: null,
  brand_primary: null,
  brand_secondary: null,
  logo_asset_ref: null,
  scene: Object.freeze({
    asset_ref: null,
    media_type: "none",
    fit: "cover",
    scrim_alpha: 0,
    blur_dp: 0,
    idle_policy: "pause_when_idle",
  }),
  motion_level: "standard",
  layout_profile: "auto",
  layout_override: null,
});

const clone = (value) => JSON.parse(JSON.stringify(value));

const trimmedOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
};

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const createPresentationModel = (presentation = null) => {
  const source = presentation && typeof presentation === "object" ? clone(presentation) : {};
  return {
    ...clone(PRESENTATION_DEFAULTS),
    ...source,
    scene: {
      ...clone(PRESENTATION_DEFAULTS.scene),
      ...(source.scene && typeof source.scene === "object" ? source.scene : {}),
    },
    layout_override:
      source.layout_override && typeof source.layout_override === "object"
        ? { ...source.layout_override }
        : null,
  };
};

export const isLogicalReference = (value) => {
  const ref = trimmedOrNull(value);
  if (!ref) return true;
  if (ref.length > 128) return false;
  if (/^(?:[a-z]:[\\/]|[\\/]|https?:\/\/|s3:\/\/)/i.test(ref)) return false;
  if (ref.includes("\\") || ref.includes("/") || ref.includes("..")) return false;
  if (!/^[a-z0-9][a-z0-9._-]*$/i.test(ref)) return false;
  const forbiddenTokens = new Set([
    "demo", "device", "example", "local", "placeholder", "sample",
    "rk3568", "rk3576", "ltp568", "ltp576",
  ]);
  return !ref.toLowerCase().split(/[._-]+/).some((token) => forbiddenTokens.has(token));
};

export const validatePresentation = (input) => {
  const presentation = createPresentationModel(input);
  const errors = {};
  const oneOf = (value, options) => options.includes(value);
  const refFields = [
    "avatar_package_ref",
    "avatar_package_version",
    "brand_reference",
    "logo_asset_ref",
  ];

  if (Number(presentation.schema_version) !== 1) {
    errors.schema_version = "Only presentation policy version 1 is supported.";
  }

  if (!oneOf(presentation.presenter_mode, ["mascot", "photoreal"])) {
    errors.presenter_mode = "Choose mascot or photoreal presenter.";
  }
  if (presentation.presenter_mode === "photoreal" && !trimmedOrNull(presentation.avatar_package_ref)) {
    errors.avatar_package_ref = "A logical package reference is required for a photoreal presenter.";
  }
  refFields.forEach((field) => {
    if (!isLogicalReference(presentation[field])) {
      errors[field] = "Use a logical reference, not a URL or device path.";
    }
  });
  if (trimmedOrNull(presentation.avatar_package_version)?.length > 64) {
    errors.avatar_package_version = "Package version must be 64 characters or fewer.";
  }
  if (!oneOf(presentation.theme_mode, ["system", "light", "dark"])) {
    errors.theme_mode = "Choose system, light or dark theme.";
  }
  ["brand_primary", "brand_secondary"].forEach((field) => {
    const value = trimmedOrNull(presentation[field]);
    if (value && !/^#[0-9a-f]{6}$/i.test(value)) {
      errors[field] = "Use a six-digit hex colour such as #F15A24.";
    }
  });
  if (!oneOf(presentation.motion_level, ["reduced", "standard"])) {
    errors.motion_level = "Choose reduced or standard motion.";
  }
  if (!oneOf(presentation.layout_profile, ["auto", "inventory_bubble", "basic_hero"])) {
    errors.layout_profile = "Choose a supported layout profile.";
  }

  const scene = presentation.scene;
  if (!oneOf(scene.media_type, ["none", "image"])) {
    errors["scene.media_type"] = "Only no scene or a static image is supported.";
  }
  if (scene.media_type === "image" && !trimmedOrNull(scene.asset_ref)) {
    errors["scene.asset_ref"] = "A logical asset reference is required for a static image.";
  }
  if (!isLogicalReference(scene.asset_ref)) {
    errors["scene.asset_ref"] = "Use a logical asset reference, not a URL or device path.";
  }
  if (!oneOf(scene.fit, ["cover", "contain"])) {
    errors["scene.fit"] = "Choose cover or contain.";
  }
  if (!oneOf(scene.idle_policy, ["pause_when_idle", "static"])) {
    errors["scene.idle_policy"] = "Choose pause when idle or static.";
  }
  const scrim = numberOrNull(scene.scrim_alpha);
  if (scrim === null || scrim < 0 || scrim > 0.85) {
    errors["scene.scrim_alpha"] = "Scrim opacity must be between 0 and 0.85.";
  }
  const blur = numberOrNull(scene.blur_dp);
  if (blur === null || blur < 0 || blur > 32) {
    errors["scene.blur_dp"] = "Blur must be between 0 and 32 dp.";
  }

  if (presentation.layout_override) {
    const scale = numberOrNull(presentation.layout_override.scale);
    const offsetX = numberOrNull(presentation.layout_override.offset_x);
    const offsetY = numberOrNull(presentation.layout_override.offset_y);
    if (scale === null || scale < 0.65 || scale > 1.6) {
      errors["layout_override.scale"] = "Scale must be between 0.65 and 1.60.";
    }
    if (offsetX === null || offsetX < -0.5 || offsetX > 0.5) {
      errors["layout_override.offset_x"] = "Horizontal offset must be between -0.50 and 0.50.";
    }
    if (offsetY === null || offsetY < -0.5 || offsetY > 0.5) {
      errors["layout_override.offset_y"] = "Vertical offset must be between -0.50 and 0.50.";
    }
  }

  return errors;
};

export const serializePresentation = (input, original = null) => {
  const presentation = createPresentationModel({
    ...(original && typeof original === "object" ? original : {}),
    ...input,
    scene: {
      ...(original?.scene && typeof original.scene === "object" ? original.scene : {}),
      ...(input?.scene && typeof input.scene === "object" ? input.scene : {}),
    },
    layout_override: input?.layout_override,
  });

  presentation.schema_version = 1;
  presentation.avatar_package_ref =
    presentation.presenter_mode === "photoreal"
      ? trimmedOrNull(presentation.avatar_package_ref)
      : null;
  presentation.avatar_package_version =
    presentation.presenter_mode === "photoreal"
      ? trimmedOrNull(presentation.avatar_package_version)
      : null;
  presentation.brand_reference = trimmedOrNull(presentation.brand_reference);
  presentation.brand_primary = trimmedOrNull(presentation.brand_primary)?.toUpperCase() || null;
  presentation.brand_secondary = trimmedOrNull(presentation.brand_secondary)?.toUpperCase() || null;
  presentation.logo_asset_ref = trimmedOrNull(presentation.logo_asset_ref);
  presentation.scene.asset_ref =
    presentation.scene.media_type === "image"
      ? trimmedOrNull(presentation.scene.asset_ref)
      : null;
  presentation.scene.scrim_alpha = Number(presentation.scene.scrim_alpha);
  presentation.scene.blur_dp = Number(presentation.scene.blur_dp);

  if (presentation.layout_override) {
    presentation.layout_override = {
      ...presentation.layout_override,
      scale: Number(presentation.layout_override.scale),
      offset_x: Number(presentation.layout_override.offset_x),
      offset_y: Number(presentation.layout_override.offset_y),
    };
  }

  return presentation;
};

export const presentationPreviewSummary = (input) => {
  const presentation = createPresentationModel(input);
  const presenter =
    presentation.presenter_mode === "photoreal"
      ? `Photoreal: ${trimmedOrNull(presentation.avatar_package_ref) || "package required"}`
      : "Ravo mascot";
  const scene =
    presentation.scene.media_type === "image"
      ? `Static scene: ${trimmedOrNull(presentation.scene.asset_ref) || "asset required"}`
      : "Tenant/default scene";
  return { presenter, scene };
};
